import { useMutation, useQueryClient } from '@tanstack/react-query';
import { sendInvitation, InvitationPayload, getGroupChatParticipants, getGroupChatShareToken, joinGroup } from '../services/group-services';
import { useConfig } from 'hooks/use-config';
import { useToast } from 'hooks/use-toast';
import { useTranslation } from 'react-i18next';
import { useGlobalMutation, useGlobalQuery } from 'state/query-client/hooks';




export const useInviteUsers = (options?: { onSuccess?: () => void }) => {
    const { toast } = useToast();
    const { t } = useTranslation();
    const { configurationSettings } = useConfig();
    const queryClient = useQueryClient();

    return useGlobalMutation({
        mutationKey: ['inviteUsers'],
        mutationFn: (data: InvitationPayload) => sendInvitation(data, configurationSettings),
        onSuccess: (data) => {
            queryClient.invalidateQueries({ queryKey: ['inviteUsers'] });
            queryClient.invalidateQueries({ queryKey: ['groupChatParticipants'] });
            queryClient.invalidateQueries({ queryKey: ['conversation', data?.conversationId] });

            options?.onSuccess?.();
        },
        onError: (error: any) => {
            const errorData = error?.error as any;
            if (errorData?.errors?.Name === 'Name already exists') {
                toast({
                    variant: 'destructive',
                    title: t('SOMETHING_WENT_WRONG'),
                    description: t('INVITE_USERS_FAILED'),
                });
            } else {
                toast({
                    variant: 'destructive',
                    title: t('SOMETHING_WENT_WRONG'),
                    description: error?.error?.message ?? t('INVITE_USERS_FAILED'),
                });
            }
        },
    });
};

export const useRemoveInvitedUser = (options?: { onSuccess?: () => void }) => {
    const { toast } = useToast();
    const { t } = useTranslation();
    const { configurationSettings } = useConfig();
    // const queryClient = useQueryClient();

    return useGlobalMutation({
        mutationKey: ['removeInvitedUser'],
        mutationFn: (data: InvitationPayload) => sendInvitation(data, configurationSettings),
        onSuccess: async (data) => {
            if (data?.conversationId) {
                // await queryClient.invalidateQueries({ queryKey: ['removeInvitedUser'] }); 
                // queryClient.invalidateQueries({ queryKey: ['groupChatParticipants'] });
                toast({
                    variant: 'success',
                    title: t('SUCCESS'),
                    description: t('REMOVE_INVITED_USER_SUCCESS'),
                });

                options?.onSuccess?.();

            }
        },
        onError: (error: any) => {
            const errorData = error?.error as any;
            if (errorData?.errors?.Name === 'Name already exists') {
                toast({
                    variant: 'destructive',
                    title: t('SOMETHING_WENT_WRONG'),
                    description: t('REMOVE_INVITED_USER_FAILED'),
                });
            } else {
                toast({
                    variant: 'destructive',
                    title: t('SOMETHING_WENT_WRONG'),
                    description: errorData?.detail ?? t('FAILED_TO_REMOVE_USER'),
                });
            }
        },
    });
};


export const useInviteAssistant = (options?: { onSuccess?: () => void }) => {
    const { toast } = useToast();
    const { t } = useTranslation();
    const { configurationSettings } = useConfig();

    return useGlobalMutation({
        mutationKey: ['inviteAssistant'],
        mutationFn: (data: InvitationPayload) => sendInvitation(data, configurationSettings),
        onSuccess: () => {
            options?.onSuccess?.();
        },
        onError: (error: any) => {
            const errorData = error?.error as any;
            toast({
                variant: 'destructive',
                title: t('SOMETHING_WENT_WRONG'),
                description: error?.error?.message ?? errorData?.detail ?? t('FAILED_TO_INVITE_ASSISTANT'),
            });
        },
    });
};

export const useGetGroupChatParticipants = (conversationId: string, enabled?: boolean) => {
    const { configurationSettings } = useConfig();
    return useGlobalQuery({
        queryKey: ['groupChatParticipants', conversationId],
        queryFn: () => getGroupChatParticipants(conversationId, configurationSettings),
        enabled: enabled !== undefined ? enabled : !!conversationId, // Only run the query when conversationId is available and enabled is true
    });
}

export const useGetGroupChatShareToken = () => {
    const { configurationSettings } = useConfig();
    const { toast } = useToast();
    const { t } = useTranslation();

    return useMutation({
        mutationFn: async (conversationId: string) => await getGroupChatShareToken(conversationId, configurationSettings),
        onError: (error: any) => {
            toast({
                variant: 'destructive',
                title: t('ERROR'),
                description: t('FAILED_TO_GET_SHARE_TOKEN'),
            });
        }
    })
}

export const useJoinGroup = (options?: { onSuccess?: (data: any) => void, onError?: (error: any) => void }) => {
    const { toast } = useToast();
    const { t } = useTranslation();
    const { configurationSettings } = useConfig();
    const queryClient = useQueryClient();

    return useGlobalMutation({
        mutationKey: ['joinGroup'],
        mutationFn: (token: string) => joinGroup(token, configurationSettings),
        onSuccess: (data) => {
            queryClient.invalidateQueries({ queryKey: ['groupChatParticipants'] });

            // toast({
            //     variant: 'success',
            //     title: t('SUCCESS'),
            //     description: t('JOINED_GROUP_SUCCESSFULLY'),
            // });

            options?.onSuccess?.(data);
        },
        onError: (error: any) => {
            options?.onError?.(error);
        },
    });
};